import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import SideDrawer from '../../../../../../common/_custom/SideDrawer/SideDrawer';
import FileViewerDrawer from '../../../../../../common/DocumentViewer/FileViewerDrawer';
import { useDocIdFileViewer } from '../../../../../../common/DocumentViewer/useDocIdFileViewer';
import { useIsMobile } from '../../../../../../common/_custom/Hooks/useIsMobile';
import { KEYBOARD_EVENTS } from '../../../../../../utils/constants/commonConstants';
import { CREDIT_RATING_DRAWER_WIDTH, DRAWER_TITLE_SUFFIX } from '../CreditReport/CreditReportConstants';
import type { ProbeCreditRatingAgency } from '../CreditReport/creditReport.types';
import CreditRatingDatePanel from './components/CreditRatingDatePanel/CreditRatingDatePanel';
import styles from './CreditRatingAgencyDrawer.module.scss';

interface CreditRatingAgencyDrawerProps {
    agency: ProbeCreditRatingAgency;
    companyName: string;
    onClose: () => void;
}

function CreditRatingAgencyDrawer({ agency, companyName, onClose }: CreditRatingAgencyDrawerProps) {
    const isMobile = useIsMobile();
    const viewer = useDocIdFileViewer();
    const isViewerOpen = !!viewer.viewerProps;
    const title = `${agency.agencyDisplay} ${DRAWER_TITLE_SUFFIX}`;
    // Mounted only while open, so this marks the start of SideDrawer's entrance.
    const drawerMountedAtRef = useRef(performance.now());

    // FileViewerDrawer sits outside sideDrawerStack, so without this capture-phase stop one Esc closes both drawers.
    useEffect(() => {
        if (!isViewerOpen) return;
        const closeViewerOnly = (event: KeyboardEvent) => {
            if (event.key !== KEYBOARD_EVENTS.ESCAPE) return;
            event.stopPropagation();
            viewer.close();
        };
        window.addEventListener('keydown', closeViewerOnly, true);
        return () => window.removeEventListener('keydown', closeViewerOnly, true);
    }, [isViewerOpen, viewer.close]);

    const heading = (
        <span className={styles.Heading}>
            <strong className={styles.HeadingTitle}>{title}</strong>
            <span className={styles.HeadingDivider} aria-hidden="true" />
            <span className={styles.HeadingName}>{companyName}</span>
        </span>
    );

    return createPortal(
        <>
            <SideDrawer
                width={CREDIT_RATING_DRAWER_WIDTH}
                showHeading={!isMobile}
                heading={heading}
                headerVariant="tight"
                ariaLabel={`${title} ${companyName}`}
                onClose={onClose}
            >
                <SideDrawer.MobileLayout>
                    {isMobile && (
                        <SideDrawer.MobileHeader title={title} status={<span className={styles.HeadingName}>{companyName}</span>} />
                    )}
                    <div className={styles.Body}>
                        <div className={styles.PanelList}>
                            {agency.dates.map((ratingDate, index) => (
                                <CreditRatingDatePanel
                                    key={`${ratingDate.date}-${index}`}
                                    ratingDate={ratingDate}
                                    onOpenReferenceDocument={viewer.open}
                                    drawerMountedAt={drawerMountedAtRef.current}
                                />
                            ))}
                        </div>
                    </div>
                </SideDrawer.MobileLayout>
            </SideDrawer>
            {viewer.viewerProps && <FileViewerDrawer {...viewer.viewerProps} />}
        </>,
        document.body,
    );
}

export default CreditRatingAgencyDrawer;
